import { useEffect, useMemo, useState } from 'react';

import {
  featuresApi,
  EmbedOnboardingConfig,
  EmbedIndustryContent,
  EmbedDemoConfig,
} from '@/lib/api/features';
import type { Industry, Onboarding } from '@/stores/embed-store';

// In-memory cache shared across hook instances (fetched once per page load).
let embedConfigCache: EmbedOnboardingConfig | null = null;
let embedConfigFetchPromise: Promise<EmbedOnboardingConfig> | null = null;

// Generic fallback — mirrors the backend code default so the page renders
// sensible content while the config loads or if the request fails.
const defaultEmbedConfig: EmbedOnboardingConfig = {
  default: {
    chips: [
      {
        label: 'Lobby dashboard',
        icon: 'LayoutDashboard',
        prompt: 'Turn my spreadsheet into a polished dashboard for our lobby screens',
      },
      {
        label: 'Safety display',
        icon: 'FileWarning',
        prompt: 'Redesign my PDF safety manual as a clean, readable signage display',
      },
      {
        label: 'Patient kiosk',
        icon: 'Users',
        prompt: 'Build a patient waiting room kiosk showcasing our doctors',
      },
      {
        label: 'Welcome screen',
        icon: 'CloudSun',
        prompt: 'Create a welcome screen for our office with live weather and news',
      },
    ],
    demos: [],
  },
  industries: {},
  aliases: {},
};

// When nothing matches (the generic `default` case), the examples section + Meet
// modal would be empty — `default` has no demos. Instead we show a curated mix:
// one representative live demo from each of these consumer-relatable categories,
// so an unknown industry still sees real screens. Excludes religious/nonprofit by
// design. Easy to reorder/extend.
const SHOWCASE_KEYS = ['restaurant', 'retail', 'manufacturing', 'hospitality', 'logistics'];

/** One representative demo (preferring a deployed one with a screenshot) from
 *  each showcase category — a varied "real screens" mix for the generic case. */
function showcaseMix(config: EmbedOnboardingConfig): EmbedDemoConfig[] {
  return SHOWCASE_KEYS.map((key) => {
    const demos = config.industries[key]?.demos ?? [];
    return demos.find((d) => d.deployUrl || d.thumbnailUrl) ?? demos[0];
  }).filter((d): d is EmbedDemoConfig => Boolean(d));
}

/** Resolved content for the current industry, with all fields populated. */
export interface ResolvedEmbedContent {
  /** The matched key: an industry slug, or 'default' when nothing matched. */
  industryKey: string;
  /** Human-readable industry name for headers (only set for a matched industry). */
  displayName?: string;
  chips: NonNullable<EmbedIndustryContent['chips']>;
  demos: NonNullable<EmbedIndustryContent['demos']>;
  featuredUseCase?: string;
}

/**
 * Resolve the content key from the available signals, in priority order:
 *   1. industry.primary    (inferred classification — highest coverage)
 *   2. industry.secondary
 *   3. onboarding.useCases  (the user's explicit picks — fills the gap when
 *      industry is absent or has no content for that slug)
 *
 * Industry leads because it has the widest coverage and maps 1:1 to content
 * keys; onboarding is the fallback that keeps new-account owners (who often
 * have a survey but no industry classification yet) personalized. Each
 * candidate matches a direct `industries` key, then an `aliases` entry — so the
 * onboarding ids that differ in spelling (`fitness`, `worship`,
 * `transportation`…) resolve via the same alias map, and screen-purpose ids
 * with no content (`safety`, `dashboards`…) simply fall through. Falls back to
 * 'default'. `reason` / `audience` are never used for resolution.
 */
function resolveIndustryKey(
  config: EmbedOnboardingConfig,
  industry: Industry | null,
  onboarding: Onboarding | null,
): string {
  const candidates = [
    industry?.primary,
    industry?.secondary,
    ...(onboarding?.useCases ?? []),
  ].filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  for (const raw of candidates) {
    const slug = raw.trim().toLowerCase();
    if (config.industries[slug]) return slug;
    const aliased = config.aliases?.[slug];
    if (aliased && config.industries[aliased]) return aliased;
  }
  return 'default';
}

/** Merge an industry's content over the default so partial overrides work. */
function mergeContent(
  config: EmbedOnboardingConfig,
  key: string,
): ResolvedEmbedContent {
  const base = config.default ?? {};
  const industry = key === 'default' ? {} : config.industries[key] ?? {};
  return {
    industryKey: key,
    displayName: key === 'default' ? undefined : industry.displayName,
    chips: industry.chips ?? base.chips ?? [],
    demos: industry.demos ?? base.demos ?? [],
    featuredUseCase: industry.featuredUseCase ?? base.featuredUseCase,
  };
}

/**
 * Hook returning industry-personalized embed onboarding content.
 *
 * Fetches the config once (cached in memory) and resolves it against the
 * provided industry, falling back to the onboarding survey's use-cases when
 * industry is absent or unmatched. Non-blocking enrichment: while loading or on
 * error it returns the generic `default` content, so the page never shows a
 * spinner for personalization.
 */
export function useEmbedContent(
  industry: Industry | null,
  onboarding: Onboarding | null,
): {
  content: ResolvedEmbedContent;
  loading: boolean;
} {
  const [config, setConfig] = useState<EmbedOnboardingConfig>(
    embedConfigCache || defaultEmbedConfig,
  );
  const [loading, setLoading] = useState(!embedConfigCache);

  useEffect(() => {
    if (embedConfigCache) {
      setConfig(embedConfigCache);
      setLoading(false);
      return;
    }

    if (!embedConfigFetchPromise) {
      embedConfigFetchPromise = featuresApi.getEmbedOnboarding();
    }

    let cancelled = false;
    embedConfigFetchPromise
      .then((data) => {
        embedConfigCache = data;
        if (!cancelled) {
          setConfig(data);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch embed onboarding content:', error);
        if (!cancelled) {
          setConfig(defaultEmbedConfig);
          setLoading(false);
        }
      })
      .finally(() => {
        embedConfigFetchPromise = null;
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const content = useMemo(() => {
    const key = resolveIndustryKey(config, industry, onboarding);
    const resolved = mergeContent(config, key);
    // Generic case: the seeded `default` carries placeholder idea demos (lobby
    // welcome / spreadsheet / announcements). Replace them with a real-category
    // showcase mix so the examples section + Meet modal show actual deployed
    // screens. (Falls back to the default demos only if the mix is empty.)
    if (key === 'default') {
      const mix = showcaseMix(config);
      if (mix.length > 0) {
        return { ...resolved, demos: mix };
      }
    }
    return resolved;
  }, [config, industry, onboarding]);

  return { content, loading };
}
